import { axiosClient } from '../client/axiosClient';

export interface CreateAnswerRequest {
  questionId: string;
  value: any;
  forEachContextValue?: string;
  parentContextId?: string;
}

export interface UpdateAnswerRequest {
  value: any;
}

export interface AnswerDto {
  id: string;
  questionId: string;
  inspectionProgramItemId: string;
  value: any;
  forEachContextValue?: string;
  parentContextId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * AnswerService handles CRUD operations for answers within the inspection hierarchy.
 *
 * URL Pattern: /api/v1/inspections/{inspectionId}/programs/{programId}/items/{itemId}/answers
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The orgId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 */
class AnswerService {
  /**
   * Get all answers for an inspection program item.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param programId - Inspection program UUID
   * @param itemId - Inspection program item UUID
   */
  getAnswers(
    _orgId: string,
    inspectionId: string,
    programId: string,
    itemId: string
  ) {
    const path = `/api/v1/inspections/${inspectionId}/programs/${programId}/items/${itemId}/answers`;
    return axiosClient.get<AnswerDto[]>(path);
  }

  /**
   * Save (create/update) answers for an inspection program item.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param programId - Inspection program UUID
   * @param itemId - Inspection program item UUID
   * @param answers - Array of answer data
   */
  saveAnswers(
    _orgId: string,
    inspectionId: string,
    programId: string,
    itemId: string,
    answers: CreateAnswerRequest[]
  ) {
    const path = `/api/v1/inspections/${inspectionId}/programs/${programId}/items/${itemId}/answers`;
    return axiosClient.post<AnswerDto[]>(path, answers);
  }

  /**
   * Update a specific answer.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param programId - Inspection program UUID
   * @param itemId - Inspection program item UUID
   * @param answerId - Answer UUID
   * @param data - Update data
   */
  updateAnswer(
    _orgId: string,
    inspectionId: string,
    programId: string,
    itemId: string,
    answerId: string,
    data: UpdateAnswerRequest
  ) {
    const path = `/api/v1/inspections/${inspectionId}/programs/${programId}/items/${itemId}/answers/${answerId}`;
    return axiosClient.put<AnswerDto>(path, data);
  }

  /**
   * Delete (soft delete) a specific answer.
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param programId - Inspection program UUID
   * @param itemId - Inspection program item UUID
   * @param answerId - Answer UUID
   */
  deleteAnswer(
    _orgId: string,
    inspectionId: string,
    programId: string,
    itemId: string,
    answerId: string
  ) {
    const path = `/api/v1/inspections/${inspectionId}/programs/${programId}/items/${itemId}/answers/${answerId}`;
    return axiosClient.delete(path);
  }

  /**
   * Batch retrieval: get answers for multiple inspection program items.
   * Organization context comes from X-Organization-Id header.
   *
   * @param _orgId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param itemIds - Array of inspection program item IDs
   * @returns Map of itemId to array of AnswerDtos
   */
  getByItemIds(_orgId: string, itemIds: string[]) {
    const path = `/api/v1/answers/batch`;
    return axiosClient.post<Record<string, AnswerDto[]>>(path, { itemIds });
  }
}

export const answerService = new AnswerService();
